import type { Organization, SimulationDifficulty, SimulationScenario } from "@scoach/types";

export interface CompanyScenarioContext {
  name: string;
  domain?: string;
  description?: string;
  productsServices?: string;
  valueProposition?: string;
  targetAudience?: string;
  competitors?: string;
  pitchKeywords?: string;
}

export function generateCompanyScenarios(
  company?: CompanyScenarioContext | Organization | null,
): SimulationScenario[] {
  const companyName = company?.name?.trim() || "SellSynq";
  const products = company?.productsServices?.trim() || "Enterprise AI Revenue & Sales Intelligence Platform";
  const valueProp =
    company?.valueProposition?.trim() ||
    "Real-time AI coaching, automated CRM sync, and battlecards that increase win rates";
  const competitors = company?.competitors?.trim() || "market incumbents";
  const primaryCompetitor = competitors.split(/[,;/]/)[0]?.trim() || "the incumbent competitor";

  const productsFirstTag = products.split(/[,;.\n]/)[0]?.trim().slice(0, 24) || "Core Solutions";

  return [
    {
      id: "company-discovery-pitch",
      title: "First Value Pitch & Problem Discovery",
      subtitle: `Introduce ${companyName} to an interested buyer exploring modernization`,
      persona: "Daniel Cohen",
      personaRole: "VP of Operations",
      company: "Northbridge Global Logistics",
      industry: "Supply Chain & Logistics",
      difficulty: "beginner" as SimulationDifficulty,
      gcpFocus: [productsFirstTag, "Discovery Pitch", "Workflow Fit"],
      hiddenObjections: [
        "We already have established routines and manual workflows; switching feels disruptive.",
        "Our executive leadership requires proof of quantifiable time savings and adoption before budget approval.",
        "I need a concrete explanation of how this fits into our day-to-day work rather than high-level buzzwords.",
      ],
      personalityTraits: ["curious", "outcome-focused", "asks for real-world examples", "cautious about team adoption"],
      stage: "Discovery",
      scenarioDescription: `A first discovery call with an operations VP. They know about operational bottlenecks in their team and are exploring what ${companyName} offers. They need to understand the concrete value before bringing it to executive leadership.`,
      systemPromptOverlay: `You are evaluating ${companyName} (${products}). You have bottlenecks, but you are skeptical of marketing hype. Ask questions like: 'How does ${companyName} integrate into our daily routine?', 'How long does onboarding take?', and 'What measurable ROI have similar teams achieved?'. If the rep cites '${valueProp}', challenge them to explain what that actually looks like in practice.`,
    },
    {
      id: "company-competitor-headtohead",
      title: "Overcoming Tough Competitor Preference",
      subtitle: `Win an executive who is actively evaluating ${primaryCompetitor}`,
      persona: "Rachel Stern",
      personaRole: "Chief Technology Officer",
      company: "Apex Media Enterprise",
      industry: "Digital Media & Technology",
      difficulty: "intermediate" as SimulationDifficulty,
      gcpFocus: [`vs ${primaryCompetitor.slice(0, 18)}`, "Differentiation", "Migration Safety"],
      hiddenObjections: [
        `We are already testing ${primaryCompetitor} and our team has momentum there.`,
        `They have a larger market footprint — why should we take a chance on ${companyName}?`,
        "We need written commitments on security, data privacy, and reliability.",
      ],
      personalityTraits: [
        "analytical",
        "vendor-savvy",
        "asks for direct head-to-head comparisons",
        "weighs switching risk carefully",
      ],
      stage: "Discovery",
      scenarioDescription: `An executive leader evaluating solutions who is currently leaning towards ${primaryCompetitor}. They agreed to hear ${companyName}'s pitch but want to know why they should choose you over the incumbent.`,
      systemPromptOverlay: `You are leaning towards ${primaryCompetitor}. Challenge the rep immediately: 'We are already reviewing ${primaryCompetitor} — what makes ${companyName} distinct, and why shouldn't we just stick with the established brand?' Push on ${valueProp}. Only consider moving forward if the rep articulates concrete, undeniable advantages.`,
    },
    {
      id: "company-cfo-roi-defense",
      title: "CFO Budget & ROI Defense",
      subtitle: "Defend pricing and build an airtight financial business case",
      persona: "Michael Park",
      personaRole: "Chief Financial Officer",
      company: "Vanguard Retail Holdings",
      industry: "Retail & Commerce",
      difficulty: "intermediate" as SimulationDifficulty,
      gcpFocus: ["Pricing Defense", "ROI Payback", "TCO Analysis"],
      hiddenObjections: [
        "We have an executive freeze on non-essential software expenditure this fiscal quarter.",
        "I need evidence that this pays for itself within 6 months.",
        "Your proposal looks expensive compared to our current allocated software spend.",
      ],
      personalityTraits: [
        "numbers-driven",
        "impatient with fluff",
        "focused strictly on hard dollar ROI and payback periods",
      ],
      stage: "Negotiation",
      scenarioDescription: `A high-stakes commercial meeting with a CFO who scrutinizes every budget line. The operational team likes ${companyName}, but the CFO is skeptical of the price tag and demands hard financial justification.`,
      systemPromptOverlay: `You are the CFO. Cut straight to financial fundamentals: 'Software budgets are tight. What is the total cost of ownership for ${companyName}, and how quickly will we see measurable revenue growth or cost savings?' Dismiss vague answers like 'team productivity'. Demand specific numbers, packaging options, and proof of ROI.`,
    },
    {
      id: "company-skeptical-tech-lead",
      title: "The Skeptical Technical Decision Maker",
      subtitle: "Convince a protective engineering & security lead who prefers building in-house",
      persona: "Sarah Chen",
      personaRole: "VP of Engineering & Security",
      company: "NovaPay Global Systems",
      industry: "Financial Technology",
      difficulty: "advanced" as SimulationDifficulty,
      gcpFocus: ["Integrations & APIs", "Security & SOC2", "Data Isolation"],
      hiddenObjections: [
        "Our engineers prefer building custom internal tools rather than bringing in another SaaS vendor.",
        "We have strict compliance, SOC2, and data isolation mandates.",
        "I am concerned about API stability, latency, and vendor lock-in.",
      ],
      personalityTraits: [
        "deeply technical",
        "skeptical of sales promises",
        "asks detailed architectural questions",
      ],
      stage: "Discovery",
      scenarioDescription: `A meeting with a technical leader who can veto the deal. If the rep cannot credibly explain how ${companyName} adheres to enterprise compliance and integrates with existing systems, the opportunity is lost.`,
      systemPromptOverlay: `You are a technical leader who protects your architecture and engineering team. Demand details: 'How does your platform integrate with our existing stack?', 'Where is our customer data stored, processed, and secured?', and 'What is your SLA and disaster recovery policy?' Respect direct, accurate technical answers; reject superficial sales talk.`,
    },
    {
      id: "company-enterprise-deal-close",
      title: "Enterprise Closing & Contract Lock-In",
      subtitle: "Negotiate final enterprise contract terms and overcome last-minute hesitation",
      persona: "Amanda Ross",
      personaRole: "Senior Director of Procurement",
      company: "Global Horizon Enterprises",
      industry: "Enterprise Conglomerate",
      difficulty: "advanced" as SimulationDifficulty,
      gcpFocus: ["Contract Terms", "Pilot Rollout", "Executive Sponsor"],
      hiddenObjections: [
        "We want a 60-day low-commitment pilot before executing an annual contract.",
        "We require custom enterprise SLA clauses with financial penalties for downtime.",
        "Our legal counsel has requested redlines on liability caps and intellectual property protections.",
      ],
      personalityTraits: [
        "seasoned negotiator",
        "strategic",
        "authoritative",
        "focused on risk mitigation and win-win terms",
      ],
      stage: "Negotiation",
      scenarioDescription: `The final commercial negotiation call. The team evaluation was positive, but procurement is pushing hard on discounts, payment terms, and phased rollout milestones before signing.`,
      systemPromptOverlay: `You are the head of procurement finalizing the contract with ${companyName}. Test the rep: 'We are prepared to move forward today, but only if you can meet our terms on rollout milestones, pricing tiers, and SLA guarantees.' See if the rep can close the deal effectively while defending value.`,
    },
  ];
}
